// Shared content types and utilities for API responses

/**
 * Sanitizes a slug to prevent SSRF attacks.
 * Only allows alphanumeric characters, hyphens, and underscores.
 * Blocks URLs, path traversal, and other malicious inputs.
 */
export function sanitizeSlug(slug: string | undefined | null): string {
  if (!slug || typeof slug !== 'string') {
    return ''
  }

  // Block URL protocols (SSRF prevention)
  if (slug.includes('://') || slug.includes('//')) {
    return ''
  }

  // Block path traversal
  if (slug.includes('..')) {
    return ''
  }

  // Remove leading slashes
  let sanitized = slug.replace(/^\/+/, '')

  // Only allow safe characters: alphanumeric, hyphen, underscore
  // This effectively blocks any URL or path manipulation
  sanitized = sanitized.replace(/[^a-zA-Z0-9-_/]/g, '')

  return sanitized
}

export interface Seo {
  index?: boolean
  meta_title?: string
  meta_description?: string
}

export interface Similar {
  slug: string
  title: string
  url: string
}

export interface Category {
  id: string
  title: string
  slug?: string
}

/**
 * Validates that a URL is safe using a whitelist approach.
 * Only allows known safe protocols and relative paths.
 * Returns '#' for anything else to prevent XSS attacks.
 */
export function sanitizeUrl(url: string | undefined | null): string {
  if (!url || typeof url !== 'string') {
    return '#'
  }

  const trimmedUrl = url.trim()
  const lowerUrl = trimmedUrl.toLowerCase()

  // Allow relative URLs and safe protocols (whitelist approach)
  if (
    trimmedUrl.startsWith('/') ||
    trimmedUrl.startsWith('#') ||
    lowerUrl.startsWith('http://') ||
    lowerUrl.startsWith('https://') ||
    lowerUrl.startsWith('mailto:') ||
    lowerUrl.startsWith('tel:')
  ) {
    return url
  }

  // Block everything else to be safe
  return '#'
}
